"use client";

import { useEffect, useState, useRef, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Image from "next/image";

const PreloaderContent = () => {
    const [isLoading, setIsLoading] = useState(true);
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const prevPathnameRef = useRef<string | null>(null);

    useEffect(() => {
        const handleLoad = () => {
            setTimeout(() => {
                setIsLoading(false);
            }, 100);
        };

        if (document.readyState === "complete") {
            setIsLoading(true);
            setTimeout(() => {
                setIsLoading(false);
            }, 100);
        } else {
            window.addEventListener("load", handleLoad);
        }

        return () => {
            window.removeEventListener("load", handleLoad);
        };
    }, []);

    useEffect(() => {
        // Only reset scroll if pathname actually changed (not on initial mount)
        const prevPathname = prevPathnameRef.current;
        const hasPathnameChanged = prevPathname !== null && prevPathname !== pathname;
        
        if (hasPathnameChanged && typeof window !== 'undefined') {
            // Small delay to ensure we're on the new page before resetting scroll
            // This prevents scroll reset on the old page
            const resetTimer = setTimeout(() => {
                // Reset scroll position to top on the NEW page only
                window.scrollTo(0, 0);
                
                // Also reset Lenis scroll position if available
                const lenis = (window as Window & { lenis?: { scrollTo: (position: number, options?: { immediate?: boolean }) => void } }).lenis;
                if (lenis && typeof lenis.scrollTo === 'function') {
                    try {
                        lenis.scrollTo(0, { immediate: true });
                    } catch {
                        // Fallback if immediate option is not supported
                        lenis.scrollTo(0);
                    }
                }
            }, 0); // Use 0ms timeout to run after navigation completes but before paint

            return () => clearTimeout(resetTimer);
        }
        
        // Update ref for next comparison
        prevPathnameRef.current = pathname;
    }, [pathname, searchParams]);

    useEffect(() => {
        setIsLoading(true);
        
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 50); // Reduced from 300ms for faster page transitions

        return () => clearTimeout(timer);
    }, [pathname, searchParams]);

    if (!isLoading) return null;

    return (
        <>
            <div className="preloader">
                <div className="spinner-wrap">
                    <div className="preloader-logo">
                        <Image
                            height={29}
                            width={177}
                            src="/images/logo-2.svg"
                            alt="preloader logo"
                            className="img-fluid"
                        />
                    </div>
                    <div className="spinner" />
                </div>
            </div>
        </>
    );
};

const Preloader = () => {
    return (
        <Suspense fallback={null}>
            <PreloaderContent />
        </Suspense>
    );
};

export default Preloader;
